/** @jsxImportSource theme-ui */
import React from 'react';
import { SxProp } from 'theme-ui';

import { LocationResiliencyToVulnerability, Vulnerability } from 'models/BepiModels';
import theme from 'theme';

import { Typography } from '../../Typography';

type BdLocationVulnerabilityIconProps = SxProp & {
  /** A number between 0 & 100 to show the resiliency */
  locationResiliency: number;
  locationName?: string;
  /** Changes the color for being placed on a dark background */
  dark?: boolean;
  labelPosition?: 'top' | 'bottom';
  /** Displays the icon only if true */
  simple?: boolean;
  /** A detailed description that gets displayed relative to the resiliency */
  showDescription?: boolean;
};

export const BdLocationVulnerabilityIcon: React.FC<BdLocationVulnerabilityIconProps> = ({
  locationResiliency,
  locationName,
  dark = false,
  labelPosition = 'bottom',
  simple = false,
  showDescription = false,
  ...props
}) => {
  if (locationResiliency == null) {
    return (
      <NoDataAvailableIcon
        {...props}
        dark={dark}
        labelPosition={labelPosition}
        showLabel={!simple}
      />
    );
  }

  return (
    <BadgeIcon
      {...props}
      locationResiliency={locationResiliency}
      locationName={locationName}
      dark={dark}
      vulnerability={LocationResiliencyToVulnerability(locationResiliency)}
      labelPosition={labelPosition}
      showLabel={!simple}
      showDescription={showDescription}
    />
  );
};

type NoDataAvailableIconProps = {
  dark: boolean;
  labelPosition: 'top' | 'bottom';
  showLabel: boolean;
};
const NoDataAvailableIcon: React.FC<NoDataAvailableIconProps> = ({
  dark,
  labelPosition,
  showLabel,
  ...props
}) => {
  const circleColour = theme.colors.stone50;
  const iconColour = dark ? theme.colors.stone20 : theme.colors.stone70;

  const noDataAvailableSvg = (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle opacity="0.15" cx="18" cy="18" r="15" fill={circleColour} />
      <path
        d="M20.3972 11.5967C19.7745 11.1299 19.0424 10.8548 18.2817 10.8015C17.5212 10.7399 16.7598 10.9075 16.0852 11.2851C15.4064 11.6583 14.8377 12.2227 14.4412 12.9165C14.0448 13.6103 13.8361 14.4066 13.8381 15.2181C13.8269 15.3105 13.8343 15.4043 13.86 15.4934C13.8856 15.5825 13.9289 15.6648 13.9868 15.7347C14.0447 15.8047 14.1161 15.8607 14.196 15.8991C14.276 15.9375 14.3627 15.9573 14.4505 15.9573C14.5382 15.9573 14.6249 15.9375 14.7049 15.8991C14.7848 15.8607 14.8562 15.8047 14.9141 15.7347C14.9721 15.6648 15.0153 15.5825 15.0409 15.4934C15.0666 15.4043 15.0741 15.3105 15.0629 15.2181C15.0666 14.6468 15.2133 14.0868 15.488 13.5955C15.7716 13.1108 16.174 12.7171 16.652 12.4564C17.1283 12.1922 17.6643 12.0733 18.2004 12.1132C18.7365 12.153 19.2517 12.3499 19.6887 12.6821C20.1363 13.0037 20.4877 13.455 20.7009 13.9823C20.9121 14.507 20.9856 15.0825 20.9135 15.6479C20.8399 16.2132 20.619 16.7454 20.2758 17.1846C19.942 17.6295 19.4962 17.9648 18.9902 18.1517C18.5224 18.3256 18.1172 18.6497 17.8306 19.0791C17.544 19.5085 17.3904 20.0218 17.3909 20.548V20.8704C17.3909 21.0443 17.456 21.211 17.5718 21.3339C17.6876 21.4568 17.8446 21.5259 18.0084 21.5259C18.1722 21.5259 18.3292 21.4568 18.445 21.3339C18.5608 21.211 18.6258 21.0443 18.6258 20.8704V20.548C18.6229 20.2935 18.6975 20.0447 18.8384 19.8388C18.9729 19.6282 19.1683 19.4697 19.3951 19.3875C20.1078 19.1214 20.7405 18.6576 21.2272 18.0442C21.701 17.426 22.0087 16.6835 22.118 15.8951C22.2358 15.0796 22.1372 14.2454 21.8333 13.4862C21.5293 12.7269 21.032 12.0726 20.3972 11.5967Z"
        fill={iconColour}
      />
      <path
        d="M17.5492 22.6073C17.4066 22.7025 17.296 22.842 17.2329 23.0062C17.167 23.1667 17.15 23.3445 17.1842 23.5154C17.2164 23.6871 17.2986 23.8443 17.4194 23.9652C17.5372 24.0932 17.6903 24.1793 17.8573 24.2113H18.0195C18.1307 24.2093 18.2406 24.1863 18.3439 24.1435C18.4997 24.0797 18.6328 23.967 18.7251 23.821C18.8002 23.6753 18.8366 23.5111 18.8305 23.3457C18.8445 23.1801 18.8118 23.0139 18.7364 22.8677C18.6611 22.7214 18.5463 22.6015 18.4064 22.5229C18.2665 22.4442 18.1076 22.4103 17.9494 22.4253C17.7913 22.4404 17.6408 22.5036 17.5167 22.6073H17.5492Z"
        fill={iconColour}
      />
    </svg>
  );
  return (
    <div
      sx={{
        display: 'flex',
      }}
      {...props}
    >
      <div sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {noDataAvailableSvg}
      </div>
      {showLabel && (
        <div
          sx={{
            display: 'flex',
            flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
            ml: '7px',
          }}
        >
          <Typography
            variant={labelPosition === 'bottom' ? 'h6' : 'subtitle2'}
            color={dark ? 'stone10' : 'stone90'}
            marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
          >
            No data available
          </Typography>
          <Typography
            variant="caption"
            color={dark ? 'deepSea40' : 'stone70'}
            marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
          >
            Epidemic preparedness index
          </Typography>
        </div>
      )}
    </div>
  );
};

type BadgeIconProps = {
  locationResiliency: number;
  locationName: string;
  dark: boolean;
  vulnerability: Vulnerability;
  labelPosition: 'top' | 'bottom';
  showLabel: boolean;
  showDescription: boolean;
};
const BadgeIcon: React.FC<BadgeIconProps> = ({
  locationResiliency,
  locationName,
  vulnerability,
  dark,
  labelPosition,
  showLabel,
  showDescription,
  ...props
}) => {
  let iconColour: string;
  let circleColour: string;

  switch (vulnerability) {
    case 'low': {
      iconColour = dark ? theme.colors.stone20 : theme.colors.stone70;
      circleColour = theme.colors.stone50;
      break;
    }
    case 'moderate': {
      iconColour = dark ? theme.colors.saffron20 : theme.colors.saffron70;
      circleColour = theme.colors.saffron70;
      break;
    }
    default: {
      iconColour = dark ? theme.colors.starfish40 : theme.colors.starfish100;
      circleColour = theme.colors.starfish100;
      break;
    }
  }

  const badgeSvg = (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle opacity="0.15" cx="18" cy="18" r="15" fill={circleColour} />
      <path
        d="M25.9841 13.6252C25.9659 13.1475 25.9659 12.6882 25.9659 12.2473C25.9659 11.8982 25.6925 11.6226 25.3462 11.6226C22.6671 11.6042 20.6259 10.8326 18.931 9.17913C18.694 8.94029 18.2931 8.94029 18.0562 9.17913C16.3612 10.8326 14.32 11.6042 11.6591 11.6042C11.3128 11.6042 11.0395 11.8798 11.0395 12.2289C11.0395 12.6698 11.0395 13.1291 11.0212 13.6252C10.9301 18.1631 10.8025 24.3544 18.2931 26.9633C18.366 26.9816 18.4207 27 18.4936 27C18.5665 27 18.6394 26.9816 18.694 26.9633C26.1846 24.3526 26.057 18.1631 25.9841 13.6252Z"
        stroke={iconColour}
      />
    </svg>
  );

  return (
    <div data-testid="vulnerability-icon" {...props}>
      <div
        sx={{
          display: 'flex',
        }}
      >
        <div sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {badgeSvg}
        </div>
        {showLabel && (
          <div
            sx={{
              display: 'flex',
              flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
              ml: '7px',
            }}
          >
            <Typography
              data-testid="location-vulnerability-score"
              variant={labelPosition === 'bottom' ? 'h6' : 'subtitle2'}
              color={dark ? 'stone10' : 'stone90'}
              marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
            >
              {`${locationResiliency}/100`}
            </Typography>
            <Typography
              variant="caption"
              color={dark ? 'deepSea40' : 'stone70'}
              marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
            >
              Epidemic preparedness index ({locationName})
            </Typography>
          </div>
        )}
      </div>
      {showLabel && showDescription && (
        <div
          data-testid="locationVulnerabilityDescription"
          sx={{
            marginTop: '10px',
            fontSize: '10px',
            lineHeight: '13px',
            color: `${dark ? 'stone10' : 'stone90'}`,
          }}
        >
          Relative to other locations globally, {locationName} has{' '}
          <b>{vulnerability} vulnerability</b> to the potential spread and impact of infectious
          diseases.
        </div>
      )}
    </div>
  );
};

export default BdLocationVulnerabilityIcon;
